import * as SearchSuggestionClick from '@ori-events/search-suggestion-click';
import { Link } from '@ori-ui/mui';
import { ContentType, Placement } from '@ori/analytics-data';
import { useCulture } from '@ori/culture';
import useTestId from '@ori/testid-generator';
import { useCallback, type FC } from 'react';
import { Fragment } from 'react';

import { useTranslations } from '../../hooks/useTranslations';
import { analytics, getProductUrl, getQuickBuyButtonOnClickHandler, goToPage } from '../../utils';
import { getSearchUrl } from '../../utils/getSearchUrl';
import { SearchPreviewHeader, SearchPreviewProduct, SearchPreviewSuggestion } from './components';
import { SEARCH_PREVIEW, SEARCH_PREVIEW_SEE_ALL_RESULTS } from './constants';
import { Inner, Products, Root, SeeAllResults, StyledDivider, Suggestions } from './styles';
import type { SearchProduct, SearchPreviewProps } from './types';
import { useSendNavigationSignal } from './utils';

export const SearchPreview: FC<SearchPreviewProps> = ({ numberOfProducts, products, suggestions, query }) => {
  const { getTestId } = useTestId();
  const { formatMessage } = useTranslations();
  const { financialSet } = useCulture();
  const { sendNavigationSignal } = useSendNavigationSignal({ query });

  const onSuggestionsClick = useCallback(
    (suggestion: string, position: number) => () => {
      window.dispatchEvent(
        new CustomEvent<SearchSuggestionClick.Data>(SearchSuggestionClick.Name, {
          detail: {
            itemData: {
              target: getSearchUrl(suggestion),
              localBannerTitle: suggestion,
              media: [],
            },
            itemType: ContentType.PopularQuery,
            searchQuery: query,
            placement: Placement.TopNavigationSearchSuggestions,
            position,
          },
        }),
      );
      goToPage(getSearchUrl(suggestion));
    },
    [query],
  );

  const onProductLick = useCallback(
    (product: SearchProduct, position: number) => async () => {
      const searchSuggestionClickData: SearchSuggestionClick.Data = {
        itemData: analytics.formatProduct({
          ...product,
          currencyCode: financialSet?.currencyCode,
        }),
        itemType: ContentType.Product,
        searchQuery: query,
        placement: Placement.TopNavigationSearchSuggestions,
        position,
      };
      window.dispatchEvent(
        new CustomEvent<SearchSuggestionClick.Data>(SearchSuggestionClick.Name, {
          detail: searchSuggestionClickData,
        }),
      );
      await sendNavigationSignal(position);
      goToPage(getProductUrl(product.code));
    },
    [financialSet?.currencyCode, query, sendNavigationSignal],
  );

  return (
    <Root data-testid={getTestId(SEARCH_PREVIEW)}>
      <Inner>
        {suggestions.length ? (
          <Suggestions>
            {suggestions.map(({ text }, index) => (
              <SearchPreviewSuggestion
                key={text}
                text={text}
                searchPhrase={query}
                onClick={onSuggestionsClick(text, index)}
              />
            ))}
          </Suggestions>
        ) : null}
        <SearchPreviewHeader query={query} />
        {products.length ? (
          <Products>
            {products.map((product, index) => (
              <Fragment key={product.code}>
                <SearchPreviewProduct
                  key={product.code}
                  index={index}
                  {...product}
                  searchPhrase={query}
                  onProductClick={onProductLick(product, index)}
                  onQuickBuyButtonClick={getQuickBuyButtonOnClickHandler(product)}
                />
                {products.length - 1 === index ? null : <StyledDivider />}
              </Fragment>
            ))}
          </Products>
        ) : null}
      </Inner>
      <SeeAllResults>
        <Link
          fontWeight="bold"
          textTransform="uppercase"
          color="textPrimary"
          data-testid={getTestId(SEARCH_PREVIEW_SEE_ALL_RESULTS)}
          href={getSearchUrl(query)}
        >
          {formatMessage('seeAllResultsFor', { count: numberOfProducts })}
        </Link>
      </SeeAllResults>
    </Root>
  );
};
